import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Loader from './Loader';
import SearchableSelect from './SearchableSelect';

const parseDateInput = (value) => new Date(`${value}T00:00:00`);

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const shiftMonths = (date, months) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
};

const getDefaultDateRange = () => {
  const toDate = new Date();
  const fromDate = shiftMonths(toDate, -1);
  return {
    from: formatDateInput(fromDate),
    to: formatDateInput(toDate)
  };
};

const isRangeOverThreeMonths = (from, to) => {
  const fromDate = parseDateInput(from);
  const toDate = parseDateInput(to);
  const maxToDate = shiftMonths(fromDate, 3);
  return toDate > maxToDate;
};

const hasTimezoneInfo = (value) => /(?:Z|[+-]\d{2}:\d{2})$/.test(value);

const formatAuditTimestamp = (value) => {
  if (!value) return '-';
  const normalized = typeof value === 'string' && !hasTimezoneInfo(value) ? `${value}Z` : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const buildUtcRangeParams = (from, to) => {
  const fromLocal = parseDateInput(from);
  const toExclusiveLocal = parseDateInput(to);
  toExclusiveLocal.setDate(toExclusiveLocal.getDate() + 1);

  return {
    from: fromLocal.toISOString(),
    to: toExclusiveLocal.toISOString()
  };
};

const parseChangesJson = (rawChanges) => {
  if (!rawChanges || typeof rawChanges !== 'string') return null;
  try {
    return JSON.parse(rawChanges);
  } catch {
    return null;
  }
};

const unwrapAuditValue = (value) => {
  if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'new')) {
    return value.new;
  }
  return value;
};

const pickEntityIdentifier = (entityName, parsedChanges) => {
  if (!parsedChanges || typeof parsedChanges !== 'object') return null;

  const keysByEntity = {
    Asset: ['SaralAssetId', 'SerialNumber', 'Name', 'Model', 'Brand'],
    Employee: ['EmployeeId', 'Name', 'Email', 'Mobile'],
    ExternalEmployee: ['EmployeeId', 'Name', 'Email', 'Mobile'],
    User: ['Username', 'Email'],
    Department: ['Name'],
    Vendor: ['Name'],
    AssetType: ['Name'],
    Discom: ['Name'],
    AssetRepair: ['IssueDescription'],
    AssetDeadItem: ['SaralAssetId', 'SerialNumber']
  };

  const keys = keysByEntity[entityName] || ['Name', 'Title', 'Code'];
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(parsedChanges, key)) {
      const value = unwrapAuditValue(parsedChanges[key]);
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        return String(value);
      }
    }
  }

  return null;
};

const formatEntityReference = (log) => {
  if (!log) return '-';
  const prefix = log.entityId ? `#${log.entityId}` : '-';
  const parsedChanges = parseChangesJson(log.changes);
  const identifier = pickEntityIdentifier(log.entityName, parsedChanges);
  if (!identifier) return prefix;
  return `${prefix} (${identifier})`;
};

function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [showDatePanel, setShowDatePanel] = useState(false);
  const [dateError, setDateError] = useState('');
  const [sortField, setSortField] = useState('time');
  const [sortDirection, setSortDirection] = useState('desc');
  const [copiedLogId, setCopiedLogId] = useState(null);
  const defaultRange = getDefaultDateRange();
  const [filters, setFilters] = useState({
    entity: '',
    action: '',
    username: '',
    from: defaultRange.from,
    to: defaultRange.to
  });

  useEffect(() => {
    document.title = 'Audit Logs - Saral Asset Manager';
  }, []);

  const fetchLogs = async (targetPage = page, sourceFilters = filters) => {
    if (!sourceFilters.from || !sourceFilters.to) {
      setDateError('From and To dates are required.');
      return;
    }
    if (parseDateInput(sourceFilters.from) > parseDateInput(sourceFilters.to)) {
      setDateError('From date cannot be after To date.');
      return;
    }
    if (isRangeOverThreeMonths(sourceFilters.from, sourceFilters.to)) {
      setDateError('Date range cannot be more than 3 months.');
      return;
    }

    setDateError('');
    setLoading(true);
    try {
      const rangeParams = buildUtcRangeParams(sourceFilters.from, sourceFilters.to);
      const response = await axios.get('/api/auditlogs', {
        params: {
          page: targetPage,
          pageSize,
          entity: sourceFilters.entity || undefined,
          action: sourceFilters.action || undefined,
          username: sourceFilters.username || undefined,
          from: rangeParams.from,
          to: rangeParams.to,
          sortBy: sortField,
          sortDir: sortDirection
        }
      });

      setLogs(response.data?.items || []);
      setTotal(response.data?.total || 0);
      setPage(response.data?.page || targetPage);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortDirection('asc');
  };

  const handleCopyChanges = async (logId, changes) => {
    if (!changes) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(changes);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = changes;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setCopiedLogId(logId);
      window.setTimeout(() => {
        setCopiedLogId((current) => (current === logId ? null : current));
      }, 1500);
    } catch (error) {
      console.error('Error copying audit log changes:', error);
    }
  };

  useEffect(() => {
    fetchLogs(1, filters);
  }, [sortField, sortDirection]);

  const applyQuickRange = (days) => {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(toDate.getDate() - days);
    setFilters({
      ...filters,
      from: formatDateInput(fromDate),
      to: formatDateInput(toDate)
    });
    setDateError('');
  };

  const applyQuickMonthRange = (months) => {
    const toDate = new Date();
    const fromDate = shiftMonths(toDate, -months);
    setFilters({
      ...filters,
      from: formatDateInput(fromDate),
      to: formatDateInput(toDate)
    });
    setDateError('');
  };

  const dateRangeLabel = filters.from && filters.to
    ? `${filters.from} to ${filters.to}`
    : 'Select date range';
  const maxToDate = filters.from ? formatDateInput(shiftMonths(parseDateInput(filters.from), 3)) : undefined;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="container-fluid">
      <div className="page-header mb-4">
        <h1 className="page-title mb-0">Audit Logs</h1>
      </div>

      <div className="card border-0 shadow-sm mb-3 audit-filters-card">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-2">
              <label className="form-label">Entity</label>
              <input
                className="form-control"
                value={filters.entity}
                onChange={(e) => setFilters({ ...filters, entity: e.target.value })}
                placeholder="Asset, User..."
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Action</label>
              <SearchableSelect
                options={[
                  { value: '', label: 'All' },
                  { value: 'Create', label: 'Create' },
                  { value: 'Update', label: 'Update' },
                  { value: 'Delete', label: 'Delete' }
                ]}
                value={filters.action}
                onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                placeholder="All"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Username</label>
              <input
                className="form-control"
                value={filters.username}
                onChange={(e) => setFilters({ ...filters, username: e.target.value })}
              />
            </div>
            <div className="col-md-4 position-relative audit-date-range">
              <label className="form-label">Date Range</label>
              <button
                className="btn btn-outline-secondary w-100 text-start d-flex justify-content-between align-items-center"
                type="button"
                onClick={() => setShowDatePanel(!showDatePanel)}
              >
                <span>{dateRangeLabel}</span>
                <i className={`fas fa-chevron-${showDatePanel ? 'up' : 'down'}`}></i>
              </button>
              {showDatePanel && (
                <div
                  className="border rounded p-2 bg-light position-absolute start-0 end-0 shadow audit-date-panel"
                  style={{ top: 'calc(100% + 6px)', zIndex: 1050 }}
                >
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => applyQuickRange(7)}>Last 7 days</button>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => applyQuickRange(30)}>Last 30 days</button>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => applyQuickMonthRange(1)}>Last 1 month</button>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => applyQuickMonthRange(3)}>Last 3 months</button>
                  </div>
                  <div className="row g-2">
                    <div className="col-6">
                      <label className="form-label small mb-1">From</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={filters.from}
                        onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                        max={filters.to || undefined}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small mb-1">To</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={filters.to}
                        onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                        min={filters.from || undefined}
                        max={maxToDate}
                      />
                    </div>
                  </div>
                  <small className="text-muted d-block mt-2">Maximum allowed range is 3 months.</small>
                </div>
              )}
            </div>
            <div className="col-md-2 d-flex align-items-end gap-2">
              <button className="btn btn-primary w-100" onClick={() => fetchLogs(1, filters)}>Apply</button>
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  const range = getDefaultDateRange();
                  const reset = { entity: '', action: '', username: '', from: range.from, to: range.to };
                  setFilters(reset);
                  setShowDatePanel(false);
                  setDateError('');
                  setTimeout(() => fetchLogs(1, reset), 0);
                }}
              >
                Reset
              </button>
            </div>
          </div>
          {dateError && (
            <div className="mt-2 text-danger small">{dateError}</div>
          )}
        </div>
      </div>

      <div className="card border-0 shadow-sm audit-grid-card">
        <div className="card-body p-0">
          {loading ? (
            <div className="p-4">
              <Loader text="Loading audit logs..." size="medium" />
            </div>
          ) : (
            <div className="table-responsive audit-log-table-wrap">
              <table className="table table-striped mb-0 audit-log-table">
                <thead>
                  <tr>
                    <th className="audit-col-time" role="button" onClick={() => handleSort('time')} style={{ cursor: 'pointer' }}>
                      Time
                      {sortField === 'time' && (
                        <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th className="audit-col-user" role="button" onClick={() => handleSort('user')} style={{ cursor: 'pointer' }}>
                      User
                      {sortField === 'user' && (
                        <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th className="audit-col-action" role="button" onClick={() => handleSort('action')} style={{ cursor: 'pointer' }}>
                      Action
                      {sortField === 'action' && (
                        <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th className="audit-col-entity" role="button" onClick={() => handleSort('entity')} style={{ cursor: 'pointer' }}>
                      Entity
                      {sortField === 'entity' && (
                        <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th className="audit-col-entity-id" role="button" onClick={() => handleSort('entityId')} style={{ cursor: 'pointer' }}>
                      Entity ID
                      {sortField === 'entityId' && (
                        <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th className="audit-col-changes">Changes</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4 text-muted">No audit logs found.</td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log.id}>
                        <td className="audit-col-time">{formatAuditTimestamp(log.createdAt)}</td>
                        <td className="audit-col-user">{log.username}</td>
                        <td className="audit-col-action">
                          <span className={`badge ${
                            log.action === 'Create' ? 'bg-success' :
                            log.action === 'Update' ? 'bg-warning text-dark' :
                            'bg-danger'
                          }`}>
                            {log.action}
                          </span>
                        </td>
                        <td className="audit-col-entity">{log.entityName}</td>
                        <td className="audit-col-entity-id">{formatEntityReference(log)}</td>
                        <td className="audit-col-changes">
                          <div className="audit-log-changes-cell" title={log.changes || '-'}>
                            <span className="audit-log-changes-text">
                              {log.changes || '-'}
                            </span>
                            {log.changes && (
                              <button
                                type="button"
                                className="audit-log-copy-button"
                                onClick={() => handleCopyChanges(log.id, log.changes)}
                                title={copiedLogId === log.id ? 'Copied' : 'Copy full text'}
                                aria-label={copiedLogId === log.id ? 'Copied' : 'Copy full text'}
                              >
                                <i className={`fas ${copiedLogId === log.id ? 'fa-check' : 'fa-copy'}`}></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="card-footer">
          {totalPages > 1 && (
            <nav>
              <ul className="pagination justify-content-center mb-0">
                <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => fetchLogs(1)} disabled={page === 1}>
                    <i className="fas fa-angle-double-left"></i>
                  </button>
                </li>
                <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => fetchLogs(page - 1)} disabled={page === 1}>
                    <i className="fas fa-angle-left"></i>
                  </button>
                </li>
                {(() => {
                  const maxVisible = 5;
                  const halfVisible = Math.floor(maxVisible / 2);
                  let startPage = Math.max(1, page - halfVisible);
                  let endPage = Math.min(totalPages, startPage + maxVisible - 1);

                  if (endPage - startPage < maxVisible - 1) {
                    startPage = Math.max(1, endPage - maxVisible + 1);
                  }

                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <li key={i} className={`page-item ${page === i ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => fetchLogs(i)}>{i}</button>
                      </li>
                    );
                  }
                  return pages;
                })()}
                <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => fetchLogs(page + 1)} disabled={page === totalPages}>
                    <i className="fas fa-angle-right"></i>
                  </button>
                </li>
                <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => fetchLogs(totalPages)} disabled={page === totalPages}>
                    <i className="fas fa-angle-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuditLogs;
